import React from "react";
import { useTranslation } from "react-i18next";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import { StepChangeClickedType } from "../WPRStepper";
import { useWPRContext } from "./useWPRContext";

type UseStepClickHandlerProps = {
  nextStepFn?: (() => void) | (() => Promise<void>);
  backStepFn?: () => void;
  stepChangeClickedType: StepChangeClickedType;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setStepChangeClickedType: React.Dispatch<
    React.SetStateAction<StepChangeClickedType>
  >;
  loadingText?: string;
};

export const useStepClickHandler = ({
  nextStepFn,
  backStepFn,
  setActiveStep,
  stepChangeClickedType,
  setStepChangeClickedType,
  loadingText,
}: UseStepClickHandlerProps) => {
  const { t } = useTranslation("");
  const apiErrorHandler = useApiErrorHandler();
  const { setStepLoadingText } = useWPRContext();
  React.useEffect(() => {
    if (stepChangeClickedType === "next") {
      setStepLoadingText(loadingText ?? t("general:isLoading"));
      // to allow non promise functions
      new Promise((res) => {
        res(nextStepFn?.());
      })
        .then(() => {
          setActiveStep((prev) => prev + 1);
        })
        .catch((e: Error) => {
          apiErrorHandler(e);
        })
        .finally(() => setStepLoadingText(""));
    } else if (stepChangeClickedType === "back") {
      setStepLoadingText(loadingText ?? t("general:loading"));
      new Promise((res) => {
        res(backStepFn?.());
      })
        .then(() => setActiveStep((prev) => prev - 1))
        .catch((e: Error) => {
          apiErrorHandler(e);
        })
        .finally(() => setStepLoadingText(""));
    } else if (stepChangeClickedType === "reset") {
      backStepFn?.();
      setActiveStep(0);
    }

    setStepChangeClickedType("stay");
  }, [
    apiErrorHandler,
    backStepFn,
    loadingText,
    nextStepFn,
    setActiveStep,
    setStepChangeClickedType,
    setStepLoadingText,
    stepChangeClickedType,
    t,
  ]);
};
